import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import FAQAccordion from '../components/FAQAccordion';
import { FAQPageSchema } from '../components/JsonLdSchema';

/* ── reusable tiny helpers ─────────────────────────────── */
const RedBtn = ({ to, children, style = {} }) => (
  <Link to={to} style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: '#CC0000', color: '#fff',
    padding: '12px 24px', borderRadius: 8, fontSize: 15, fontWeight: 600,
    transition: 'background 0.2s', ...style
  }}
    onMouseEnter={e => e.currentTarget.style.background = '#AA0000'}
    onMouseLeave={e => e.currentTarget.style.background = '#CC0000'}
  >{children}</Link>
);

/* infinite marquee of dashboard images */
function ImageMarquee() {
  const images = [
    '/1.webp',
    '/2.webp',
    '/3.webp',
    '/4.webp',
    '/5.webp',
  ];
  const doubled = [...images, ...images];
  return (
    <div style={{ overflow: 'hidden', width: '100%', position: 'relative' }}>
      <div style={{
        display: 'flex', gap: 16, width: 'max-content',
        animation: 'marquee 28s linear infinite',
      }}>
        {doubled.map((src, i) => (
          <div key={i} className="marquee-card">
            <img
              src={src}
              srcSet={`${src.replace('.webp', '-sm.webp')} 520w, ${src} 800w`}
              sizes="(max-width: 768px) 260px, 380px"
              alt="DPOS dashboard"
              loading={i < images.length ? 'eager' : 'lazy'}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* vertical types */
const verticals = [
  { icon: '🍽️', label: 'Fine Dine' },
  { icon: '⚡', label: 'QSR' },
  { icon: '☕', label: 'Cafe' },
  { icon: '☁️', label: 'Cloud Kitchen' },
  { icon: '🎂', label: 'Bakery' },
  { icon: '🍺', label: 'Bar & Brewery' },
];

export default function Home() {
  return (
    <main style={{ paddingTop: 64 }}>
      <SEOHead
        title="Restaurant POS & Billing Software India | DPOS"
        description="DPOS is restaurant POS and billing software for restaurants in India — billing, inventory, KOT, and online orders in one app. No migration charges."
        canonical="/"
      />

      {/* ── HERO ─────────────────────────────────────────── */}
      <section style={{
        background: 'linear-gradient(135deg, #fff 0%, #fef2f2 40%, #fff 100%)',
        padding: '80px 24px 60px', textAlign: 'center'
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 800, lineHeight: 1.1, color: '#111827', marginBottom: 16 }}>
            Restaurant<br />
            <span style={{ color: '#CC0000' }}>POS &amp; Billing Software</span>
          </h1>

          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fef2f2',
            border: '1px solid #fecaca', borderRadius: 100, padding: '6px 16px',
            fontSize: 13, fontWeight: 800, color: '#8B0000', marginBottom: 16
          }}>
            ★ 5.0 Rated by Restaurants on Google
          </div>

          <p style={{ fontSize: 16, fontWeight: 600, color: '#374151', marginBottom: 16 }}>
            Billing · Inventory · KOT · Online Ordering — All in One App
          </p>

          <p style={{ fontSize: 18, color: '#6b7280', lineHeight: 1.7, marginBottom: 32, maxWidth: 560, margin: '0 auto 32px' }}>
            Restaurant billing and management software that streamlines operations, manages
            inventory, and helps you run service without the back-office busywork — trusted
            by 100+ restaurants across India, with no migration charges to switch.
          </p>

          <RedBtn to="/contact">
            Contact for Free Demo <span style={{ fontSize: 18 }}>→</span>
          </RedBtn>

          <p style={{ marginTop: 16, fontSize: 15, color: '#4b5563', fontWeight: 700 }}>
            ✓ No Migration Charges &nbsp;·&nbsp; ✓ Free Setup &nbsp;·&nbsp; ✓ 24/7 Support
          </p>
        </div>

        {/* marquee */}
        <div style={{ marginTop: 60 }}>
          <ImageMarquee />
        </div>
      </section>

      {/* ── VERSATILITY ──────────────────────────────────── */}
      <section style={{ padding: '80px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Versatility</p>
          <h2 style={{ fontSize: 'clamp(28px, 3vw, 44px)', fontWeight: 800, color: '#111827', marginBottom: 12 }}>Restaurant POS Software for Every Format</h2>
          <p style={{ fontSize: 16, color: '#6b7280', marginBottom: 48, maxWidth: 560 }}>
            DPOS is restaurant POS software built for the Indian F&amp;B market, and it adapts to your
            specific business model — whether you run a cozy cafe, a quick-service outlet, or a bustling
            fine dining establishment. From single-outlet cafes to multi-location chains, the same
            restaurant POS software scales with you, with billing, KOT, inventory, and online ordering
            built around how Indian restaurants actually operate.
          </p>

          {/* type cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 16, marginBottom: 64 }}>
            {verticals.map(v => (
              <div key={v.label} style={{
                background: '#f9fafb', borderRadius: 12, padding: '24px 16px', textAlign: 'center',
                border: '1px solid #e5e7eb', transition: 'all 0.2s', cursor: 'default'
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#CC0000'; e.currentTarget.style.background = '#fff'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(204,0,0,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ fontSize: 32, marginBottom: 8 }}>{v.icon}</div>
                <p style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{v.label}</p>
              </div>
            ))}
          </div>

          {/* image + text */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }} className="two-col">
            <img
              src="/cafe-billing-counter.webp"
              alt="Staff using DPOS system at a cafe counter"
              loading="lazy"
              style={{ borderRadius: 16, width: '100%', objectFit: 'cover', height: 400 }}
            />
            <div>
              <h2 style={{ fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 800, color: '#111827', marginBottom: 16, lineHeight: 1.2 }}>
                Restaurant Billing Software: Complete Control at Your Fingertips
              </h2>
              <p style={{ fontSize: 16, color: '#6b7280', lineHeight: 1.7, marginBottom: 32 }}>
                DPOS's restaurant billing software manages orders, recipes, and staff from a single dashboard. Real-time sync ensures you never miss a beat.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {[
                  { icon: '🧾', title: 'Smart Billing', desc: 'Split bills, merge tables, and accept multiple payment modes effortlessly.' },
                  { icon: '📊', title: 'Advanced Analytics', desc: 'Get actionable insights on top-selling items and peak hours.' },
                ].map(f => (
                  <div key={f.title} style={{ display: 'flex', gap: 16 }}>
                    <div style={{ width: 44, height: 44, background: '#fef2f2', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{f.icon}</div>
                    <div>
                      <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 }}>{f.title}</h3>
                      <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Migration */}
      <section style={{ padding: '64px 24px', background: '#f9fafb' }}>
        <div style={{ maxWidth: 780, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, color: '#111827', marginBottom: 16 }}>
            Switching POS Systems? No Migration Charges, No Data Loss
          </h2>
          <p style={{ fontSize: 16, color: '#6b7280', lineHeight: 1.75, maxWidth: 640, margin: '0 auto' }}>
            Moving off your current POS usually comes with switching fees — DPOS doesn't charge
            them. Setup is free, so switching to restaurant POS software built for your business
            doesn't add extra cost on top of what you already pay to run your restaurant.
          </p>
        </div>
      </section>

      {/* Buying criteria */}
      <section style={{ padding: '64px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, color: '#111827', textAlign: 'center', marginBottom: 40 }}>
            What to Look for in Restaurant POS Software
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            {[
              { title: 'Billing speed', desc: 'Orders should process in seconds, even during a dinner rush.' },
              { title: 'Kitchen integration', desc: 'KOTs should route to the right station automatically, not get sorted by hand.' },
              { title: 'Delivery platform sync', desc: 'Zomato, Swiggy, and other aggregator orders should land directly in your POS.' },
              { title: 'Inventory and costing', desc: 'Stock and recipe costs should update without a separate spreadsheet.' },
              { title: 'Offline reliability', desc: 'Billing shouldn’t stop the moment your internet connection does.' },
              { title: 'Switching cost', desc: 'Check what migrating from your current system actually costs before you sign.' },
            ].map(c => (
              <div key={c.title} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 20px' }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 6 }}>{c.title}</h3>
                <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section style={{ padding: '64px 24px', background: '#f9fafb', textAlign: 'center' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, color: '#111827', marginBottom: 16 }}>
            How DPOS Compares
          </h2>
          <p style={{ fontSize: 16, color: '#6b7280', lineHeight: 1.75, marginBottom: 24 }}>
            See how DPOS stacks up against other restaurant POS software in India on features,
            support, and switching cost.
          </p>
          <Link to="/blog/dpos-vs-competitors" style={{ color: '#CC0000', fontWeight: 700, fontSize: 15 }}>
            Read the comparison →
          </Link>
        </div>
      </section>

      {/* Testimonials — real reviews from DPOS's Google Business Profile */}
      <section style={{ padding: '56px 0', background: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.12em', textAlign: 'center', marginBottom: 6 }}>
            What Restaurants Say
          </p>
          <h2 style={{ fontSize: 'clamp(22px, 2.6vw, 30px)', fontWeight: 800, color: '#111827', textAlign: 'center', marginBottom: 28 }}>
            Reviews from Google
          </h2>
          <div style={{ display: 'flex', gap: 14, overflowX: 'auto', scrollSnapType: 'x mandatory', paddingBottom: 12 }}>
            {[
              { name: 'Shabnam Sayed', text: 'DPOS has made our restaurant billing much faster and easier. The interface is simple enough for our staff to learn quickly, and the reporting gives us a clear picture of our daily sales.' },
              { name: 'Sania Khan', text: 'Excellent POS system with a user-friendly interface and reliable performance. It makes billing and restaurant management smooth and efficient. The support team is responsive and always ready to help. Highly recommended!' },
              { name: 'V Travels', text: 'The QR menu and online ordering features are especially useful for our restaurant. Orders can flow directly into the POS instead of staff manually entering everything.' },
              { name: 'Ilham Sayed', text: 'DPOS has completely simplified our restaurant billing and daily operations. The system is easy for our staff to use, and the reports help us understand our business better.' },
              { name: 'Saood Khan', text: 'Accommodating staff and great support' },
            ].map(t => (
              <div key={t.name} style={{ flex: '0 0 280px', scrollSnapAlign: 'start', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px' }}>
                <div style={{ color: '#f59e0b', fontSize: 13, marginBottom: 8, letterSpacing: 1 }}>★★★★★</div>
                <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, marginBottom: 12 }}>“{t.text}”</p>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — direct Q&A content for search snippets and AI answer engines */}
      <section style={{ padding: '80px 24px', background: '#f9fafb' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <FAQPageSchema />
          <p style={{
            fontSize: 13, fontWeight: 600, color: '#CC0000',
            textTransform: 'uppercase', letterSpacing: '0.12em',
            textAlign: 'center', marginBottom: 6,
          }}>
            Frequently Asked Questions
          </p>
          <h2 style={{
            fontSize: 'clamp(24px, 3vw, 36px)',
            fontWeight: 800, color: '#111827',
            textAlign: 'center', marginBottom: 40,
          }}>
            Everything You Need to Know About DPOS
          </h2>
          <FAQAccordion />
        </div>
      </section>

    </main>
  );
}
